<template>
  <div class="p-4  min-h-screen w-full">
    <div class="bg-transparent p-8 rounded-lg shadow-none max-w-5xl mx-auto mt-10">
      <!-- Title changes based on current step -->
      <h1 class="text-3xl font-bold mb-6 text-white">
        <template v-if="activeIndex === 0 && !showSuccess">Template</template>
        <template v-if="activeIndex === 1 && !showSuccess">Initialisation</template>
        <template v-if="activeIndex === 2 && !showSuccess">Choix de la cible</template>
        <template v-if="activeIndex === 3 && !showSuccess">Planification</template>
        <template v-if="activeIndex === 4 && !showSuccess">Récapitulatif</template>
        <template v-if="showSuccess"></template>
      </h1>
      
      <!-- Progress steps - only show if not on success screen -->
      <div v-if="!showSuccess" class="mb-8 mt-40">
        <div class="flex justify-between items-center relative">
          <!-- Progress bar -->
          <div class="absolute h-1 bg-gray-200 left-0 right-0 top-1/2 -translate-y-1/2 z-0"></div>
          <div class="absolute h-1 bg-accent left-0 right-0 top-1/2 -translate-y-1/2 z-0" 
               :style="{ width: progressWidth }"></div>
          
          <!-- Step circles -->
          <div v-for="(step, i) in steps" :key="i" 
               class="rounded-full w-10 h-10 flex items-center justify-center z-10 relative"
               :class="[i <= activeIndex ? 'bg-accent text-white' : 'bg-white border border-gray-300']">
            <i v-if="i < activeIndex" class="pi pi-check"></i>
            <span v-else>{{ i + 1 }}</span>
          </div>
        </div>
        
        <!-- Step labels -->
        <div class="flex justify-between mt-2">
          <div v-for="(step, i) in steps" :key="i" class="text-center" 
               :class="[i <= activeIndex ? 'text-cyan-500 font-medium' : 'text-gray-500']">
            {{ step.label }}
          </div>
        </div>
      </div>
      
      <!-- Success screen -->
      <div v-if="showSuccess" class="text-center py-16">
        <div class="flex justify-center mb-8">
          <div class="relative rounded-full w-24 h-24 bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center animate-bounce">
            <div class="absolute inset-1 rounded-full bg-white flex items-center justify-center">
              <i class="pi pi-check text-4xl text-cyan-500"></i>
            </div>
            <div class="absolute -inset-1 rounded-full bg-cyan-200 opacity-30 animate-ping"></div>
          </div>
        </div>
        <h2 class="text-2xl font-semibold mb-12">Votre campagne de sensibilisation a été effectué avec succès.</h2>
        <Button label="Retour" class="spect-btn-primary" size="large" @click="goToList" />
      </div>
      
      <!-- Step content - only show if not on success screen -->
      <div v-else class="step-content">
        <!-- Template selection step -->
        <div v-if="activeIndex === 0">
          <h2 class="text-xl font-semibold mb-6">Sélectionnez votre template pour la campagne</h2>
          
          <div class="grid grid-cols-3 gap-4">
            <div v-for="(template, index) in templates" :key="index" 
                 class="border rounded-lg overflow-hidden cursor-pointer transition-all hover:shadow-lg"
                 :class="{'border-cyan-500 border-2': selectedTemplate === index}"
                 @click="selectTemplate(index)">
              <div class="relative">
                <img :src="template.image" :alt="template.name" class="w-full h-48 object-cover" />
                <div class="p-3">
                  <h3 class="font-medium" :class="{'text-cyan-500': selectedTemplate === index}">
                    {{ template.name }}
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Initialisation step -->
        <div v-if="activeIndex === 1">
          <div class="mb-12">
            <div class="flex items-start mb-10">
              <img :src="selectedTemplateImage" alt="Template sélectionné" class="w-32 h-32 object-cover rounded mr-4" />
              <div>
                <h3 class="font-medium mb-1">{{ selectedTemplateName }}</h3>
              </div>
            </div>
            
            <div class="mb-10">
              <h3 class="text-lg font-medium mb-4">Type de campagne</h3>
              <div class="flex gap-8">
                <div class="flex items-center">
                  <RadioButton v-model="campaign.type" value="phishing" inputId="phishing" />
                  <label for="phishing" class="ml-2">Phishing</label>
                </div>
                <div class="flex items-center">
                  <RadioButton v-model="campaign.type" value="smishing" inputId="smishing" />
                  <label for="smishing" class="ml-2">Smishing</label>
                </div>
              </div>
              <p v-if="campaign.type === 'phishing'" class="mt-2 text-sm text-gray-600">
                Dans ce mode il sera envoyer aux utilisateurs des mails contenants des liens malveillants.
              </p>
              <p v-else class="mt-2 text-sm text-gray-600">
                Dans ce mode il sera envoyer aux utilisateurs des sms contenants des liens malveillants.
              </p>
            </div>
            
            <div class="mb-4">
              <label for="campaign-name" class="block mb-2">Entrez le nom de votre campagne</label>
              <InputText id="campaign-name" v-model="campaign.name" class="w-full p-3" placeholder="Nom campagne" />
            </div>
            
            <div class="bg-amber-50 border-l-4 border-amber-500 p-4 text-amber-800 mt-4">
              <i class="pi pi-info-circle mr-2"></i>
              Attribuez un nom unique à votre campagne d'audit dans le champ ci-dessus
            </div>
          </div>
        </div>
        
        <!-- Target selection step -->
        <div v-if="activeIndex === 2">
          <div class="mb-12">
            <div class="flex items-start mb-10">
              <img :src="selectedTemplateImage" alt="Template sélectionné" class="w-32 h-32 object-cover rounded mr-4" />
              <div>
                <h3 class="font-medium mb-1">{{ selectedTemplateName }}</h3>
              </div>
            </div>
            
            <div class="mb-10">
              <h3 class="text-lg font-medium mb-4">Cible de votre campagne</h3>
              <div class="flex gap-8">
                <div class="flex items-center">
                  <RadioButton v-model="campaign.targetType" value="group" inputId="group-target" />
                  <label for="group-target" class="ml-2">Groupe cible</label>
                </div>
                <div class="flex items-center">
                  <RadioButton v-model="campaign.targetType" value="service" inputId="service-target" />
                  <label for="service-target" class="ml-2">Service cible</label>
                </div>
              </div>
            </div>
            
            <div class="mb-8">
              <label class="block mb-2">{{ campaign.targetType === 'group' ? 'Groupe cible' : 'Service cible' }}</label>
              <Dropdown v-model="campaign.selectedTarget" :options="availableTargets" optionLabel="name" 
                        :placeholder="campaign.targetType === 'group' ? 'Sélectionner un groupe' : 'Sélectionner un service'" 
                        class="w-full" :loading="isLoading" />
            </div>
            
            <div class="mb-8">
              <label class="block mb-2">Les utilisateurs de ce {{ campaign.targetType === 'group' ? 'groupe' : 'service' }}</label>
              <div v-if="isLoadingUsers" class="flex justify-center py-4">
                <i class="pi pi-spin pi-spinner text-2xl"></i>
              </div>
              <div v-else class="border rounded-lg p-4 max-h-48 overflow-y-auto">
                <div v-if="groupUsers.length === 0" class="py-2 text-center text-gray-500">
                  Aucun utilisateur trouvé
                </div>
                <div v-for="(user, index) in groupUsers" :key="user.id" class="py-2 border-b last:border-b-0">
                  <div class="flex items-center justify-between">
                    <div class="flex-1">
                      <div class="flex items-center">
                        <span class="mr-2">{{ index + 1 }}.</span>
                        <span>{{ user.name }}</span>
                        <span v-if="user.email" class="ml-2 text-sm text-gray-500">({{ user.email }})</span>
                      </div>
                      <div v-if="campaign.type === 'smishing'" class="ml-6 text-sm">
                        <span v-if="user.phone" class="text-green-600">
                          <i class="pi pi-phone mr-1"></i>{{ user.phone }}
                        </span>
                        <span v-else class="text-red-500">
                          <i class="pi pi-exclamation-triangle mr-1"></i>Aucun numéro de téléphone
                        </span>
                      </div>
                    </div>
                    <div v-if="campaign.type === 'smishing' && !user.phone" class="ml-2">
                      <Button 
                        icon="pi pi-pencil" 
                        size="small" 
                        severity="secondary" 
                        outlined 
                        @click="editUser(user.id)"
                        v-tooltip="'Modifier l\'utilisateur'"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="bg-amber-50 border-l-4 border-amber-500 p-4 text-amber-800 mt-4">
              <i class="pi pi-info-circle mr-2"></i>
              <span v-if="campaign.type === 'phishing'">
                Seuls les membres du {{ campaign.targetType === 'group' ? 'groupe' : 'service' }} cible recevront la campagne.
                Vous pouvez modifier cette liste dans le menu de gestion des utilisateurs.
              </span>
              <span v-else>
                Seuls les membres du {{ campaign.targetType === 'group' ? 'groupe' : 'service' }} cible avec un numéro de téléphone recevront la campagne SMS.
                Les utilisateurs sans numéro de téléphone ne seront pas inclus dans la campagne.
                Vous pouvez modifier les informations des utilisateurs dans le menu de gestion des utilisateurs.
              </span>
            </div>
          </div>
        </div>
        
        <!-- Planning step -->
        <div v-if="activeIndex === 3">
          <div class="mb-12">
            <div class="flex items-start mb-10">
              <img :src="selectedTemplateImage" alt="Template sélectionné" class="w-32 h-32 object-cover rounded mr-4" />
              <div>
                <h3 class="font-medium mb-1">{{ selectedTemplateName }}</h3>
              </div>
            </div>
            
            <div class="mb-10">
              <!-- <div class="mb-8">
                <label class="block mb-2">Les utilisateurs de ce {{ campaign.targetType === 'group' ? 'groupe' : 'service' }}</label>
                <div class="border rounded-lg p-4 max-h-48 overflow-y-auto">
                  <div v-if="groupUsers.length === 0" class="py-2 text-center text-gray-500">
                    Aucun utilisateur trouvé
                  </div>
                  <div v-for="(user, index) in groupUsers" :key="user.id" class="py-2 border-b last:border-b-0">
                    <div class="flex items-center justify-between">
                      <div class="flex-1">
                        <div class="flex items-center">
                          <span class="mr-2">{{ index + 1 }}.</span>
                          <span>{{ user.name }}</span>
                        </div>
                        <div v-if="campaign.type === 'smishing'" class="ml-6 text-sm">
                          <span v-if="user.phone" class="text-green-600">
                            <i class="pi pi-phone mr-1"></i>{{ user.phone }}
                          </span>
                          <span v-else class="text-red-500">
                            <i class="pi pi-exclamation-triangle mr-1"></i>Aucun numéro de téléphone
                          </span>
                        </div>
                      </div>
                      <div v-if="campaign.type === 'smishing' && !user.phone" class="ml-2">
                        <Button 
                          icon="pi pi-pencil" 
                          size="small" 
                          severity="secondary" 
                          outlined 
                          @click="editUser(user.id)"
                          v-tooltip="'Modifier l\'utilisateur'"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div> -->
              
              <div class="mb-8">
                <label class="block mb-2">Date et heure de début de la campagne</label>
                <Calendar v-model="campaign.startDate" showIcon showTime hourFormat="24" dateFormat="dd/mm/yy" placeholder="Date et heure de début de la campagne" class="w-1/2" />
              </div>
              
              <div class="mb-8">
                <label class="block mb-2">Date et heure de fin de la campagne</label>
                <Calendar v-model="campaign.endDate" showIcon showTime hourFormat="24" dateFormat="dd/mm/yy" placeholder="Date et heure de fin de la campagne" class="w-1/2" :minDate="minEndDate" />
              </div>
            </div>
          </div>
        </div>
        
        <!-- Summary step -->
        <div v-if="activeIndex === 4">
          <div class="mb-12">
            <div class="flex items-start mb-10">
              <img :src="selectedTemplateImage" alt="Template sélectionné" class="w-32 h-32 object-cover rounded mr-4" />
              <div>
                <h3 class="font-medium mb-1">{{ selectedTemplateName }}</h3>
              </div>
            </div>
            
            <div class="mb-10">
              <h3 class="text-lg font-medium mb-2">Type de campagne</h3>
              <div class="flex items-center mb-8">
                <i class="pi pi-circle-fill text-cyan-500 mr-2 text-xs"></i>
                <span>{{ campaign.type === 'phishing' ? 'Phishing' : 'Smishing' }}</span>
              </div>
              <p v-if="campaign.type === 'phishing'" class="mb-8 text-sm text-gray-600">
                Dans ce mode il sera envoyer aux utilisateurs des mails contenants des liens malveillants.
              </p>
              
              <h3 class="text-lg font-medium mb-2">Nom campagne</h3>
              <div class="mb-8">
                <InputText v-model="campaign.name" class="w-full" readonly disabled />
              </div>
              
              <h3 class="text-lg font-medium mb-2">Cible de votre campagne</h3>
              <div class="flex items-center mb-8">
                <i class="pi pi-circle-fill text-cyan-500 mr-2 text-xs"></i>
                <span>{{ campaign.targetType === 'group' ? 'Groupe cible' : 'Service cible' }}</span>
              </div>
              
              <div class="mb-8">
                <Dropdown v-model="campaign.selectedTarget" :options="availableTargets" optionLabel="name" 
                          class="w-full" disabled />
              </div>
              
              <div class="mb-8">
                <label class="block mb-2">Les utilisateurs de ce {{ campaign.targetType === 'group' ? 'groupe' : 'service' }}</label>
                <div class="border rounded-lg p-4 max-h-48 overflow-y-auto">
                  <div v-if="groupUsers.length === 0" class="py-2 text-center text-gray-500">
                    Aucun utilisateur trouvé
                  </div>
                  <div v-for="(user, index) in groupUsers" :key="user.id" class="py-2 border-b last:border-b-0">
                    <div class="flex items-center justify-between">
                      <div class="flex-1">
                        <div class="flex items-center">
                          <span class="mr-2">{{ index + 1 }}.</span>
                          <span>{{ user.name }}</span>
                        </div>
                        <div v-if="campaign.type === 'smishing'" class="ml-6 text-sm">
                          <span v-if="user.phone" class="text-green-600">
                            <i class="pi pi-phone mr-1"></i>{{ user.phone }}
                          </span>
                          <span v-else class="text-red-500">
                            <i class="pi pi-exclamation-triangle mr-1"></i>Aucun numéro de téléphone
                          </span>
                        </div>
                      </div>
                      <div v-if="campaign.type === 'smishing' && !user.phone" class="ml-2">
                        <Button 
                          icon="pi pi-pencil" 
                          size="small" 
                          severity="secondary" 
                          outlined 
                          @click="editUser(user.id)"
                          v-tooltip="'Modifier l\'utilisateur'"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="mb-8">
                <label class="block mb-2">Date et heure de début de la campagne</label>
                <Calendar v-model="campaign.startDate" showTime hourFormat="24" disabled class="w-full" />
              </div>
              
              <div class="mb-8">
                <label class="block mb-2">Date et heure de fin de la campagne</label>
                <Calendar v-model="campaign.endDate" showTime hourFormat="24" disabled class="w-full" />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Navigation buttons - only show if not on success screen -->
      <div v-if="!showSuccess" class="flex mt-8" :class="[activeIndex > 0 ? 'justify-between' : 'justify-center']">
        <Button label="Précédent" class="spect-btn-primary" size="large" 
                @click="prevStep" :disabled="activeIndex === 0" v-show="activeIndex > 0" />
        
        <Button v-if="activeIndex < 4" label="Continuer" class="spect-btn-primary" size="large"
                @click="nextStep" :disabled="!canContinue" />
        
        <Button v-else label="Valider" class="spect-btn-primary" 
                @click="submitCampaign" :disabled="!canContinue || isSubmitting" size="large">
          <i v-if="isSubmitting" class="pi pi-spin pi-spinner mr-2"></i>
          {{ isSubmitting ? 'Traitement en cours...' : 'Valider' }}
        </Button>
      </div>
    </div>
    <PhoneUpdateDialog 
    v-model="showPhoneDialog" 
    :user="selectedUserForEdit"
    @phone-updated="onPhoneUpdated"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import Calendar from 'primevue/calendar';
import RadioButton from 'primevue/radiobutton';
import { fetchPhishingTemplates, createCampaign } from '@/services/campaignService';
import { fetchGroups, fetchGroupById } from '@/services/groupService';
import { fetchServices, fetchServiceUsers } from '@/services/serviceService';
import PhoneUpdateDialog from '@/components/PhoneUpdateDialog.vue';
//import { useToast } from 'primevue/usetoast'
const router = useRouter();

// Steps configuration
const steps = ref([
  { label: 'Template' },
  { label: 'Initialisation' },
  { label: 'Choix de la cible' },
  { label: 'Planification' },
  { label: 'Récapitulatif' }
]);

const activeIndex = ref(0);
const showSuccess = ref(false);
//const toast = useToast()
// Progress bar width calculation
const progressWidth = computed(() => {
  const totalSteps = steps.value.length - 1;
  const progress = (activeIndex.value / totalSteps) * 100;
  return `${progress}%`;
});

// Template data
const templates = ref([]);

// Fetch templates from service
const fetchTemplates = async () => {
  try {
    const fetchedTemplates = await fetchPhishingTemplates();
    if (fetchedTemplates.length > 0) {
      templates.value = fetchedTemplates;
    } else {
      // Fallback to default templates if API returns empty
      templates.value = [
        { 
          name: 'Nom template', 
          image: 'https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?q=80&w=1974&auto=format&fit=crop' 
        },
        { 
          name: 'Nom template', 
          image: 'https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?q=80&w=1974&auto=format&fit=crop' 
        }
      ];
    }
  } catch (error) {
    console.error('Error fetching templates:', error);
    // Fallback templates in case of error
    templates.value = [
      { 
        name: 'Nom template', 
        image: 'https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?q=80&w=1974&auto=format&fit=crop' 
      },
      { 
        name: 'Nom template', 
        image: 'https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?q=80&w=1974&auto=format&fit=crop' 
      }
    ];
  }
};

// Groups and services data
const groups = ref([]);
const services = ref([]);
const isLoading = ref(false);

// Fetch groups and services
const fetchTargetData = async () => {
  isLoading.value = true;
  try {
    // Fetch groups
    const fetchedGroups = await fetchGroups();
    groups.value = fetchedGroups.map(group => ({
      id: group.id,
      name: group.name,
      userCount: group.users?.length || 0
    }));
    
    // Fetch services
    const fetchedServices = await fetchServices();
    services.value = fetchedServices.map(service => ({
      id: service.id,
      name: service.name
    }));
  } catch (error) {
    console.error('Error fetching groups and services:', error);
  } finally {
    isLoading.value = false;
  }
};

// Users data
const groupUsers = ref([]);
const isLoadingUsers = ref(false);

// Fetch users when a group or service is selected
const fetchUsers = async () => {
  if (!campaign.value.selectedTarget) return;
  
  isLoadingUsers.value = true;
  groupUsers.value = [];
  
  try {
    if (campaign.value.targetType === 'group') {
      
      // Fetch group users
      const groupData = await fetchGroupById(campaign.value.selectedTarget.id);
      if (groupData && groupData.user_detail) {
        groupUsers.value = groupData.user_detail.map(user => ({
          id: user.id,
          name: `${user.firstname || ''} ${user.lastname || ''}`.trim() || 'Utilisateur sans nom',
          firstname: user.firstname,
          lastname: user.lastname,
          email: user.email,
          phone: user.phone || null
        }));
      }
    } else {
      // Fetch service users
      const serviceUsers = await fetchServiceUsers(campaign.value.selectedTarget.id);
      if (serviceUsers) {
        groupUsers.value = serviceUsers.map(user => ({
          id: user.id,
          name: `${user.firstname || ''} ${user.lastname || ''}`.trim() || 'Utilisateur sans nom',
          firstname: user.firstname,
          lastname: user.lastname,
          email: user.email,
          phone: user.phone || null
        }));
      }
    }
  } catch (error) {
    console.error(`Error fetching users for ${campaign.value.targetType}:`, error);
  } finally {
    isLoadingUsers.value = false;
  }
};

/*const editUser = (userId) => {
  console.log(userId)
  //router.push(`/gestion-utilisateurs?edit=${userId}`);
  window.location.href = "/gestion-utilisateurs";
};*/

// Call fetchTemplates and fetchTargetData when component is mounted
onMounted(() => {
  fetchTemplates();
  fetchTargetData();
});

const selectedTemplate = ref(null);
const selectedTemplateName = computed(() => {
  return selectedTemplate.value !== null ? templates.value[selectedTemplate.value].name : '';
});
const selectedTemplateImage = computed(() => {
  return selectedTemplate.value !== null ? templates.value[selectedTemplate.value].image : '';
});

// Campaign data
const campaign = ref({
  name: '',
  type: 'phishing',
  targetType: 'group',
  selectedTarget: null,
  startDate: null,
  endDate: null
});

// Watch for changes in selectedTarget to fetch users
watch(() => campaign.value.selectedTarget, (newValue) => {
  if (newValue) {
    fetchUsers();
  } else {
    groupUsers.value = [];
  }
});

// Watch for changes in targetType to reset selectedTarget
watch(() => campaign.value.targetType, () => {
  campaign.value.selectedTarget = null;
  groupUsers.value = [];
});

// Min end date (1 day after start date)
// eslint-disable-next-line
const minEndDate = computed(() => {
  if (!campaign.value.startDate) return null;
  
  const date = new Date(campaign.value.startDate);
  date.setDate(date.getDate() + 1);
  return date;
});

// Computed properties
const canContinue = computed(() => {
  if (activeIndex.value === 0) {
    return selectedTemplate.value !== null;
  } else if (activeIndex.value === 1) {
    return campaign.value.name && campaign.value.type;
  } else if (activeIndex.value === 2) {
    return campaign.value.targetType && campaign.value.selectedTarget;
  } else if (activeIndex.value === 3) {
    return campaign.value.startDate && campaign.value.endDate;
  }
  return true; // Summary step can always continue
});

// Get available targets based on targetType
const availableTargets = computed(() => {
  return campaign.value.targetType === 'group' ? groups.value : services.value;
});

// Methods
const selectTemplate = (index) => {
  selectedTemplate.value = index;
};

const nextStep = () => {
  if (activeIndex.value < steps.value.length - 1) {
    activeIndex.value++;
  }
};

const prevStep = () => {
  if (activeIndex.value > 0) {
    activeIndex.value--;
  }
};
const isSubmitting = ref(false);

const submitCampaign = async () => {
  try {
    // Indique que la soumission est en cours
    isSubmitting.value = true;
    
    // Récupère le template sélectionné
    const template = templates.value[selectedTemplate.value];
    
    // Prépare les données selon le type de cible (groupe ou service)
    const groups = [];
    let service = '';
    
    if (campaign.value.targetType === 'group') {
      groups.push(campaign.value.selectedTarget.id);
    } else {
      service = campaign.value.selectedTarget.id;
    }
    
    // Ajoute 3 heures à la date de début (comme dans le code original)
    const startDate = new Date(campaign.value.startDate);
    startDate.setHours(startDate.getHours() + 3);
    
    // Prépare le payload selon le type de campagne
    const payload = {
      name: campaign.value.name,
      gophish_landing_page_id: template.landing_page_id,
      gophish_template_id: template.template_id,
      smtp: "",
      url: "",
      organisation: localStorage.organisation,
    };
    
    // Ajoute les champs spécifiques selon le type de cible
    if (campaign.value.targetType === 'group') {
      payload.groups = groups;
    } else {
      payload.service = service;
    }
    
    // Ajoute les dates avec les noms de champs appropriés selon le type de campagne
    if (campaign.value.type === 'phishing') {
      payload.launch_date = startDate;
      payload.end_date = campaign.value.endDate;
    } else {
      payload.launch_date = startDate;
      payload.closing_date = campaign.value.endDate;
    }
    
    // Appelle l'API pour créer la campagne
    await createCampaign({
      type: campaign.value.type,
      ...payload
    });
    
    // Affiche l'écran de succès
    showSuccess.value = true;
    isSubmitting.value = false;
    
  } catch (error) {
    console.error('Error submitting campaign:', error);
    // Gestion des erreurs (vous pourriez ajouter un toast ou une notification ici)
    isSubmitting.value = false;
  }
};

const goToList = () => {
  // Navigate back to campaign list
  router.push('/audits');
};

// Add new reactive variables
const showPhoneDialog = ref(false);
const selectedUserForEdit = ref(null);

// Update the editUser function
const editUser = (userId) => {
  const user = groupUsers.value.find(u => u.id === userId);
  if (user) {
    selectedUserForEdit.value = user;
    showPhoneDialog.value = true;
  }
};

// Add phone update handler
const onPhoneUpdated = (data) => {
  // Update the user in the groupUsers array
  const userIndex = groupUsers.value.findIndex(u => u.id === data.userId);
  if (userIndex !== -1) {
    groupUsers.value[userIndex].phone = data.phone;
  }
  
  // Reset selected user
  selectedUserForEdit.value = null;
};

 

</script>

<style lang="scss" scoped>
@use "../../../assets/scss/variables.scss";
@use "../../../assets/scss/app/common.scss";
/*
:deep(.p-button) {
  border-radius: 0;
}

:deep(.p-inputtext),
:deep(.p-dropdown) {
  border-radius: 0;
}

.bg-navy-800 {
  background-color: #132c4e;
}

.bg-cyan-500 {
  background-color: #0cbcc6;
}

.text-cyan-500 {
  color: #0cbcc6;
}

.border-cyan-500 {
  border-color: #0cbcc6;
}
*/
.step-content {
  min-height: 400px;
}
</style>